import React, {useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import AmountStep from './AmountStep'
import ProviderStep from './ProviderStep'
import CheckoutStep from './CheckoutStep'
import {StripeOnboardingWithdrawModal} from './StripeOnboardingWithdrawModal'
import {moneyFromMinor} from '../../../utils/UnitConversion.utils'
import {TransactionDetailsDTO} from '../DepositWithdrawModal'
import {RootState} from '../../../../store'

type Props = {
    transactionDetails: TransactionDetailsDTO
    currency: string
    symbol: string
    rate: number
    convertFromUSD: (v: number) => number
    formatAmountWithoutCurrency: (v: number) => string
    selectedCountry: string | null
    setSelectedCountry: (country: string | null) => void
    closeModal: () => void
    kyc: string
    onOpenAddMethodModal: () => void
}

const steps = [
    { key: 'provider', label: 'Payment Provider' },
    { key: 'amount', label: 'Amount' },
    { key: 'checkout', label: 'Checkout' },
]

export const Withdraw: React.FC<Props> = ({
                                              transactionDetails,
                                              currency,
                                              symbol,
                                              rate,
                                              convertFromUSD,
                                              formatAmountWithoutCurrency,
                                              selectedCountry,
                                              setSelectedCountry,
                                              closeModal,
                                              kyc,
                                              onOpenAddMethodModal,
                                          }) => {
    const stripe = useSelector((state: RootState) => state.auth.paymentAccounts?.stripe)
    const stripeConnectEnabled = stripe?.stripeConnectEnabled ?? false

    // Extract payout methods directly from transactionDetails
    const payoutMethods = transactionDetails.payoutMethods || []
    const [selectedPayoutMethodId, setSelectedPayoutMethodId] = useState<string | null>(null)

    const [targetAmountInUsd, setTargetAmountInUsd] = useState(0)
    const [amountInputInUserCurrency, setAmountInputInUserCurrency] = useState('')
    const [hasUserEditedAmount, setHasUserEditedAmount] = useState(false)

    const allFees = transactionDetails.withdrawProviderFees

    // Determine currently selected payout method and its type
    const selectedMethod = payoutMethods.find(m => m.id === selectedPayoutMethodId)
    const methodType = selectedMethod?.methodType

    // Reset amount inputs on payout method change
    useEffect(() => {
        setTargetAmountInUsd(0)
        setAmountInputInUserCurrency('')
        setHasUserEditedAmount(false)
    }, [selectedPayoutMethodId])

    // If Stripe Connect is not enabled, render the onboarding modal component directly
    if (!stripeConnectEnabled) {
        return <StripeOnboardingWithdrawModal onClose={closeModal} />
    }

    // Determine applicable fees based on selected payout method type
    const selectedFee =
        methodType === 'BANK'
            ? allFees.find(f => f.feeType === 'OPEN_BANKING')
            : methodType === 'CARD'
                ? currency === 'EUR'
                    ? allFees.find(f => f.feeType === 'CREDIT_CARD_EUR')
                    : allFees.find(f => f.feeType === 'CREDIT_CARD_GLOBAL')
                : null

    const min = selectedFee ? convertFromUSD(moneyFromMinor(selectedFee.minAmountMinor)) : undefined
    const max = selectedFee ? convertFromUSD(moneyFromMinor(selectedFee.maxAmountMinor)) : undefined

    const isProviderValid = selectedPayoutMethodId !== null
    const isAmountValid =
        isProviderValid &&
        hasUserEditedAmount &&
        targetAmountInUsd > 0 &&
        (min === undefined || targetAmountInUsd >= min) &&
        (max === undefined || targetAmountInUsd <= max)

    const currentStepIndex = !isProviderValid ? 0 : !isAmountValid ? 1 : 2

    const getStepState = (stepKey: string) => {
        if (stepKey === 'provider') return { done: isProviderValid, active: !isProviderValid, disabled: false }
        if (stepKey === 'amount') return { done: isAmountValid, active: isProviderValid && !isAmountValid, disabled: !isProviderValid }
        return { done: false, active: isProviderValid && isAmountValid, disabled: !isProviderValid || !isAmountValid }
    }

    return (
        <div className="flex flex-col flex-1 relative overflow-y-auto">
            <div className="flex flex-col gap-6">
                {steps.map((step, i) => {
                    const { done, active, disabled } = getStepState(step.key)
                    const stepDone = i < currentStepIndex ? true : done

                    return (
                        <div key={step.key} className="flex gap-4">
                            <div className="flex flex-col items-center">
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                    stepDone ? 'bg-blue-500 text-white' : active ? 'border-2 border-blue-500 text-blue-500' : 'border-2 border-gray-400 text-gray-400'
                                }`}>
                                    {i + 1}
                                </div>
                                {i < steps.length - 1 && <div className={`w-[3px] flex-1 my-1 ${i < currentStepIndex ? 'bg-blue-500' : 'bg-gray-400'}`} />}
                            </div>

                            <div className={`flex-1 ${disabled ? 'opacity-40 pointer-events-none' : ''}`}>
                                <div className="mb-2 text-sm font-semibold">{step.label}</div>

                                {step.key === 'provider' && (
                                    <ProviderStep
                                        selectedPayoutMethodId={selectedPayoutMethodId}
                                        setSelectedPayoutMethodId={setSelectedPayoutMethodId}
                                        payoutMethods={payoutMethods}
                                        onAddNewMethod={onOpenAddMethodModal}
                                    />
                                )}

                                {step.key === 'amount' && (
                                    <AmountStep
                                        amountInputInUserCurrency={amountInputInUserCurrency}
                                        setAmountInputInUserCurrency={v => { setHasUserEditedAmount(true); setAmountInputInUserCurrency(v) }}
                                        targetAmountInUsd={targetAmountInUsd}
                                        setTargetAmountInUsd={v => { setHasUserEditedAmount(true); setTargetAmountInUsd(v) }}
                                        currency={currency}
                                        currencySymbol={symbol}
                                        convertFromUSD={convertFromUSD}
                                        formatAmountWithoutCurrency={formatAmountWithoutCurrency}
                                        minAmount={min}
                                        maxAmount={max}
                                    />
                                )}

                                {step.key === 'checkout' && (
                                    <CheckoutStep
                                        provider={methodType?.toLowerCase() as any}
                                        amountInputInUserCurrency={amountInputInUserCurrency}
                                        targetAmountInUsd={targetAmountInUsd}
                                        setModalOpen={closeModal}
                                        kyc={kyc}
                                        currency={currency}
                                        currencyRate={rate}
                                        eurRateScaled={transactionDetails.eurRateScaled}
                                        currencySymbol={symbol}
                                        transactionDetails={transactionDetails}
                                        minAmount={min}
                                        maxAmount={max}
                                    />
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default Withdraw